import styled, { css, keyframes } from 'styled-components';
import { palette, theme } from 'styled-tools';

import { buttonNormalize, fontFamily, minWidth, units } from '@calm-web/design-system';
import { ChevronLeft, ChevronRight } from '@calm-web/icons';

import { HEADER_HEIGHT_DESKTOP, HEADER_HEIGHT_MOBILE } from '../Header/styles';
import { navItemTypography } from '../NavItemLink/styles';

export type SlideFrom = 'left' | 'right';

const slideInFromRight = keyframes`
	from {
		transform: translateX(100%);
	}
	to {
		transform: translateX(0);
	}
`;

const slideInFromLeft = keyframes`
	from {
		transform: translateX(-100%);
	}
	to {
		transform: translateX(0);
	}
`;

const fadeIn = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;

export const ListViewport = styled.div<{ $isSliding: boolean }>`
	/* The flyout aligns children to flex-start; stretch so the chevron reaches the edge. */
	align-self: stretch;
	/* Matches the flyout's 16px left gutter on its list. */
	padding-right: ${units(2)};

	/* The flyout reserves only the mobile header's height above its children, so the
	   rest of the taller desktop header is added here to keep the list clear of BackButton. */
	${minWidth('desktop')} {
		padding-top: calc(${HEADER_HEIGHT_DESKTOP} - ${HEADER_HEIGHT_MOBILE});
	}

	/* A sliding list starts off the panel's edge, which would make the flyout scroll
	   sideways. Clip only mid-slide, since clipping all the time would also cut off
	   focus rings. */
	${p =>
		p.$isSliding &&
		css`
			@media (prefers-reduced-motion: no-preference) {
				overflow-x: hidden;
			}
		`}
`;

export const MobileNavWrapper = styled.ul<{ $slideFrom: SlideFrom | null }>`
	list-style: none;
	padding: 0;

	${p =>
		p.$slideFrom &&
		css`
			@media (prefers-reduced-motion: no-preference) {
				animation: ${p.$slideFrom === 'right' ? slideInFromRight : slideInFromLeft}
					${theme('animation.transition')};
			}
		`}
`;

const buttonReset = css`
	${buttonNormalize}
	color: ${palette('white')};
	cursor: pointer;
`;

const chevronStyles = css`
	flex-shrink: 0;
	height: 33px;
	width: 33px;

	path {
		fill: currentColor;
	}
`;

export const SubmenuTrigger = styled.button`
	${buttonReset}
	${fontFamily}
	${navItemTypography}
	align-items: center;
	display: flex;
	justify-content: space-between;
	width: 100%;
`;

export const SubmenuTriggerChevron = styled(ChevronRight)`
	${chevronStyles}
`;

// Spans the header's height in the flyout's top padding, so the icon centers on the
// same line as the header's close button at every header size.
export const BackButton = styled.button`
	${buttonReset}
	align-items: center;
	display: flex;
	height: ${HEADER_HEIGHT_MOBILE};
	left: ${units(2)};
	position: absolute;
	top: 0;

	${minWidth('desktop')} {
		height: ${HEADER_HEIGHT_DESKTOP};
	}

	@media (prefers-reduced-motion: no-preference) {
		animation: ${fadeIn} ${theme('animation.transition')};
	}
`;

export const BackChevron = styled(ChevronLeft)`
	${chevronStyles}
`;
